import { useState, useEffect, useMemo, useCallback } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  getDocumentOptions,
  searchDocuments as searchDocumentsApi,
  getDocumentCount,
  DocumentOption,
} from '@/api/filterService'
import { useDebounce } from './useDebounce'

const PAGE_SIZE = 50

interface UseDocumentsOptions {
  jurisdictionUuids: string[]
  pubAuthorityUuids?: string[]
  enabled?: boolean
}

/**
 * Custom hook for paginated document fetching with server-side search.
 *
 * - Fetches documents in pages of 50 via /documents/options
 * - Supports server-side fuzzy search via /documents/search (debounced 300ms)
 * - Accumulates all fetched documents (browse + search) into a deduplicated pool
 * - Provides total count via /documents/count
 */
export function useDocuments(options: UseDocumentsOptions) {
  const { jurisdictionUuids, pubAuthorityUuids, enabled = true } = options

  // Sort for stable cache keys
  const sortedJurisdictionUuids = [...jurisdictionUuids].sort()
  const sortedPubAuthorityUuids = pubAuthorityUuids ? [...pubAuthorityUuids].sort() : []

  // Search state (owned by this hook)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearch = useDebounce(searchQuery, 300)
  const isSearchActive = debouncedSearch.length > 0

  // Persistent accumulator for documents found via search
  // (survives across different search terms)
  const [searchAccumulated, setSearchAccumulated] = useState<Map<string, DocumentOption>>(new Map())

  // Seeded documents — pre-fetched by UUID (e.g. pre-selected docs from a restored conversation)
  const [seededDocuments, setSeededDocuments] = useState<Map<string, DocumentOption>>(new Map())

  const isEnabled = enabled && jurisdictionUuids.length > 0

  // --- Paginated browse ---
  const browseQuery = useInfiniteQuery({
    queryKey: ['documents-browse', sortedJurisdictionUuids, sortedPubAuthorityUuids],
    queryFn: ({ pageParam }) =>
      getDocumentOptions(jurisdictionUuids, pubAuthorityUuids, PAGE_SIZE, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined
      return allPages.length * PAGE_SIZE
    },
    initialPageParam: 0,
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

  // --- Server-side search ---
  const searchQueryResult = useInfiniteQuery({
    queryKey: ['documents-search', sortedJurisdictionUuids, sortedPubAuthorityUuids, debouncedSearch],
    queryFn: ({ pageParam }) =>
      searchDocumentsApi(jurisdictionUuids, debouncedSearch, pubAuthorityUuids, PAGE_SIZE, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined
      return allPages.length * PAGE_SIZE
    },
    initialPageParam: 0,
    enabled: isEnabled && debouncedSearch.length > 0,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

  // --- Total count ---
  const countQuery = useQuery({
    queryKey: ['documents-count', sortedJurisdictionUuids, sortedPubAuthorityUuids],
    queryFn: () => getDocumentCount(jurisdictionUuids, pubAuthorityUuids),
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

  // Merge search results into the persistent accumulator
  useEffect(() => {
    if (searchQueryResult.data) {
      const newDocs = searchQueryResult.data.pages.flat()
      if (newDocs.length > 0) {
        setSearchAccumulated((prev) => {
          const next = new Map(prev)
          let changed = false
          newDocs.forEach((doc) => {
            if (!next.has(doc.legislation_uuid)) {
              next.set(doc.legislation_uuid, doc)
              changed = true
            }
          })
          return changed ? next : prev
        })
      }
    }
  }, [searchQueryResult.data])

  // Reset accumulated search results and seeds when filters change
  useEffect(() => {
    setSearchAccumulated(new Map())
    setSeededDocuments(new Map())
    setSearchQuery('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedJurisdictionUuids.join(','), sortedPubAuthorityUuids.join(',')])

  // All unique documents we've ever fetched (browse pages + search accumulator + seeds)
  const allFetchedDocuments = useMemo(() => {
    const map = new Map<string, DocumentOption>()
    seededDocuments.forEach((doc, uuid) => map.set(uuid, doc))
    const browseDocs = browseQuery.data?.pages.flat() ?? []
    browseDocs.forEach((doc) => map.set(doc.legislation_uuid, doc))
    searchAccumulated.forEach((doc, ref) => map.set(ref, doc))
    return Array.from(map.values())
  }, [browseQuery.data, searchAccumulated, seededDocuments])

  // Seed pre-fetched documents (e.g. pre-selected docs from a restored conversation)
  const seedDocuments = useCallback((docs: DocumentOption[]) => {
    if (docs.length === 0) return
    setSeededDocuments(prev => {
      const next = new Map(prev)
      let changed = false
      docs.forEach(doc => {
        if (!next.has(doc.legislation_uuid)) {
          next.set(doc.legislation_uuid, doc)
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [])

  // What to display in the dropdown
  const displayDocuments = isSearchActive
    ? (searchQueryResult.data?.pages.flat() ?? [])
    : allFetchedDocuments

  // Pagination controls (context-dependent)
  const fetchNextPage = useCallback(() => {
    if (isSearchActive) {
      searchQueryResult.fetchNextPage()
    } else {
      browseQuery.fetchNextPage()
    }
  }, [isSearchActive, searchQueryResult, browseQuery])

  const hasNextPage = isSearchActive
    ? (searchQueryResult.hasNextPage ?? false)
    : (browseQuery.hasNextPage ?? false)

  return {
    displayDocuments,
    allFetchedDocuments,
    totalCount: countQuery.data ?? 0,
    isLoading: browseQuery.isLoading,
    isLoadingMore: browseQuery.isFetchingNextPage || searchQueryResult.isFetchingNextPage,
    isSearchLoading: searchQueryResult.isFetching && isSearchActive,
    error: browseQuery.error || searchQueryResult.error,
    fetchNextPage,
    hasNextPage,
    searchQuery,
    setSearchQuery,
    isSearchActive,
    seedDocuments,
  }
}
